'use client'

import { FC, useEffect, useState, useRef, RefObject } from 'react'
import { Panel, PanelItem, PanelLayout } from '../Panel'
import { Colour, SectionPaddingDefault } from '../constants'
import styles from './styles.module.scss'
import cx from 'classnames'
import { CmsPageSectionBase } from '../ComposedPage/types'
import { Document } from '@contentful/rich-text-types'
import { Answer } from './Answer'
import { Question } from './Question'
import { IconographyButton } from '../IconographyButton/IconographyButton'
import reload from '@/assets/icons/reload.svg'
import backArrow from '@/assets/icons/back_white.svg'
import { PanelTitles } from '../PanelTitles'
import { CmsResponsiveImage } from '../ComposedPage/types'

export interface QuestionnaireResource {
  sys: {
    id: string
    contentType: {
      sys: {
        id: string
      }
    }
  }
  fields: {
    metaTitle: string
    slug: string
    heroImage: CmsResponsiveImage
  }
}

export interface CmsOption {
  sys: {
    id: string
  }
  fields: {
    title: string
    richText: Document | null
    resources: QuestionnaireResource[] | null
    nestedQuestion?: CmsQuestionEntry
  }
}

export interface CmsQuestion {
  title: string
  options: CmsOption[]
}

export interface CmsQuestionEntry {
  sys: {
    id: string
  }
  fields: CmsQuestion
}

export interface CmsQuestionnaire extends CmsPageSectionBase {
  fields: CmsPageSectionBase['fields'] & {
    title?: string
    subtitle?: string
    richText?: Document | null
    question: CmsQuestionEntry
  }
}

export const Questionnaire: FC<CmsQuestionnaire> = ({ sys, fields }) => {
  const { question, title, subtitle } = fields
  const panelRef = useRef<HTMLDivElement>(null)
  const contentful_id = sys.id
  const [selections, setSelections] = useState<number[]>([])

  // Load selections from localStorage after mount to avoid hydration mismatch
  useEffect(() => {
    const savedSelections = window.localStorage.getItem(contentful_id!)
    if (savedSelections) {
      setSelections(JSON.parse(savedSelections))
    }
  }, [contentful_id])

  const scrollToTitle = () =>
    panelRef.current?.scrollIntoView({ behavior: 'smooth' })

  const reset = () => {
    setSelections([])
    scrollToTitle()
  }

  const back = () => {
    setSelections(prev => {
      const arr = [...prev]

      arr.pop()

      return arr
    })

    scrollToTitle()
  }

  let nextQuestion: CmsQuestion | undefined = question.fields
  let answer: CmsOption | undefined

  const setCurrentQuestionOrAnswer = () => {
    for (const selection of selections) {
      const selectedOption = nextQuestion?.options[selection]

      if (!selectedOption) {
        return
      }

      const nestedQuestionEntry = selectedOption.fields.nestedQuestion

      nextQuestion = nestedQuestionEntry?.fields

      if (!nextQuestion) {
        answer = selectedOption
      }
    }
  }

  setCurrentQuestionOrAnswer()

  useEffect(() => {
    window.localStorage.setItem(contentful_id!, JSON.stringify(selections))
  }, [selections, contentful_id])

  const selectHandler = (index: number) => {
    setSelections(prev => [...prev, index])
  }

  return (
    <Panel
      forwardRef={panelRef as RefObject<HTMLElement>}
      layout={PanelLayout.full}
      colour={Colour.warmStone}
      className={cx(
        styles.questionnaire,
        styles[SectionPaddingDefault.TOP],
        styles[SectionPaddingDefault.BOTTOM],
      )}
      short
    >
      <PanelItem>
        <PanelTitles title={title} subtitle={subtitle} />
        <>
          {answer ? (
            <Answer data={answer} />
          ) : nextQuestion ? (
            <Question data={nextQuestion} selectionHandler={selectHandler} />
          ) : null}
        </>

        {selections.length ? (
          <div className={styles.controls}>
            <IconographyButton
              value="Start over"
              handler={reset}
              icon={reload.src}
              className={styles.button}
              hollow
            />
            <IconographyButton
              value="Step back"
              handler={back}
              icon={backArrow.src}
              className={styles.button}
            />
          </div>
        ) : null}
      </PanelItem>
    </Panel>
  )
}
